/**
 * Package-aware intake form.
 * Route: /business-services/intake/:slug  (honeycomb | queen-b | empire)
 *
 * Form depth grows with the package tier. Empire requires a strategy session
 * before development begins.
 */
import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Hexagon, Crown, Sparkles, Calendar } from 'lucide-react';
import SEO from '../components/shared/SEO';

const PACKAGES = {
  honeycomb: {
    name: 'Honeycomb',
    price: 500,
    icon: Hexagon,
    timeline: '5–7 Business Days',
    description: 'Starter website — perfect for new businesses ready to claim a professional online presence.',
    sections: ['business', 'overview', 'branding', 'structure', 'expectations'],
  },
  'queen-b': {
    name: 'Queen B',
    price: 1500,
    icon: Crown,
    timeline: '2–3 Weeks',
    description: 'Business website — multi-page experience with branding, booking integration, and lead capture.',
    sections: ['business', 'overview', 'audience', 'branding', 'structure', 'content', 'functionality', 'seo', 'expectations'],
  },
  empire: {
    name: 'Empire',
    price: 3000,
    icon: Sparkles,
    timeline: '4–6 Weeks',
    description: 'Premium website with CRM, client portal, automation, and AI integration. Includes a required strategy session.',
    sections: ['business', 'overview', 'audience', 'branding', 'structure', 'content', 'functionality', 'integrations', 'automation', 'seo', 'expectations', 'strategy'],
  },
};

const ALL_SECTIONS = {
  business: {
    title: 'Business Information',
    fields: [
      { key: 'business_name', label: 'Business Name', required: true },
      { key: 'owner_name', label: 'Owner Name', required: true },
      { key: 'email', label: 'Email', required: true, type: 'email' },
      { key: 'phone', label: 'Phone', required: true, type: 'tel' },
      { key: 'website', label: 'Current Website (if any)' },
      { key: 'social', label: 'Social Media Links', textarea: true },
      { key: 'industry', label: 'Industry / Niche', required: true },
    ],
  },
  overview: {
    title: 'Business Overview',
    fields: [
      { key: 'description', label: 'Describe your business in detail', textarea: true, required: true },
      { key: 'services', label: 'Services or products offered', textarea: true, required: true },
      { key: 'usp', label: 'What makes you different from competitors?', textarea: true, required: true },
      { key: 'goals', label: 'Business goals (next 6–12 months)', textarea: true },
    ],
  },
  audience: {
    title: 'Target Audience',
    fields: [
      { key: 'ideal_customer', label: 'Describe your ideal customer', textarea: true, required: true },
      { key: 'demographics', label: 'Demographics (age, income, location)', textarea: true },
      { key: 'pain_points', label: 'What pain points do you solve?', textarea: true, required: true },
    ],
  },
  branding: {
    title: 'Branding',
    fields: [
      { key: 'has_logo', label: 'Do you have a logo? (Yes/No + describe)' },
      { key: 'brand_colors', label: 'Brand colors (hex codes if known)' },
      { key: 'fonts', label: 'Preferred fonts or font styles' },
      { key: 'inspiration_sites', label: 'Inspiration websites (URLs)', textarea: true, required: true },
      { key: 'design_preferences', label: 'Design preferences (luxury, minimal, bold, playful?)', textarea: true },
    ],
  },
  structure: {
    title: 'Website Structure',
    fields: [
      { key: 'required_pages', label: 'Pages required (Home, About, Services, etc.)', textarea: true, required: true },
      { key: 'primary_cta', label: 'Primary call-to-action (Book, Buy, Subscribe, etc.)', required: true },
      { key: 'user_journey', label: 'Desired user journey (what should visitors do first?)', textarea: true },
    ],
  },
  content: {
    title: 'Content',
    fields: [
      { key: 'has_photos', label: 'Do you have professional photos? (Yes/No)' },
      { key: 'has_videos', label: 'Do you have video content?' },
      { key: 'has_copy', label: 'Will you provide written content, or need copywriting help?' },
      { key: 'existing_materials', label: 'Other marketing materials available', textarea: true },
    ],
  },
  functionality: {
    title: 'Functionality',
    fields: [
      { key: 'booking', label: 'Do you need booking / scheduling?' },
      { key: 'forms', label: 'Custom forms beyond contact?' },
      { key: 'payments', label: 'Will you accept payments online?' },
      { key: 'crm', label: 'Need CRM integration? (Specify which)' },
      { key: 'email_marketing', label: 'Email marketing platform (Mailchimp, ConvertKit, etc.)' },
    ],
  },
  integrations: {
    title: 'Integrations (Empire)',
    fields: [
      { key: 'crm_choice', label: 'Preferred CRM (HubSpot, Pipedrive, Go High Level, etc.)' },
      { key: 'portal_needs', label: 'Client portal requirements', textarea: true },
      { key: 'third_party_apps', label: 'Other tools to integrate', textarea: true },
    ],
  },
  automation: {
    title: 'Automation & AI (Empire)',
    fields: [
      { key: 'workflows_needed', label: 'Which workflows do you want to automate?', textarea: true, required: true },
      { key: 'ai_assistant', label: 'Do you want AI features? (Chatbot, content gen, lane analyzer, etc.)', textarea: true },
      { key: 'process_docs', label: 'Existing process documentation? (We can document if not)', textarea: true },
    ],
  },
  seo: {
    title: 'SEO',
    fields: [
      { key: 'service_areas', label: 'Cities / regions you serve', textarea: true },
      { key: 'keywords', label: 'Target search keywords', textarea: true },
      { key: 'search_goals', label: 'What should people find you for?', textarea: true },
    ],
  },
  expectations: {
    title: 'Expectations',
    fields: [
      { key: 'outcome', label: 'What does success look like for this project?', textarea: true, required: true },
      { key: 'timeline', label: 'Desired launch date or timeline' },
      { key: 'budget_notes', label: 'Anything we should know about budget or add-ons?', textarea: true },
      { key: 'notes', label: 'Additional notes', textarea: true },
    ],
  },
  strategy: {
    title: 'Strategy Session (Required for Empire)',
    fields: [
      { key: 'strategy_date_pref', label: 'Preferred dates for a 60-min strategy session', textarea: true, required: true },
      { key: 'stakeholders', label: 'Who else will attend? (Names + roles)', textarea: true },
      { key: 'key_questions', label: 'Top 3 questions you want answered in the session', textarea: true },
    ],
  },
};

export default function BusinessIntakePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const pkg = PACKAGES[slug] || PACKAGES['queen-b'];
  const Icon = pkg.icon;

  const sectionKeys = pkg.sections;
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const sectionKey = sectionKeys[stepIdx];
  const section = ALL_SECTIONS[sectionKey];

  const isLast = stepIdx === sectionKeys.length - 1;
  const progress = useMemo(() => Math.round(((stepIdx + 1) / sectionKeys.length) * 100), [stepIdx, sectionKeys.length]);

  const acknowledged = data.acknowledged;

  const validateStep = () => {
    for (const f of section.fields) {
      if (f.required && !data[f.key]?.trim()) {
        setError(`Please complete: ${f.label}`);
        return false;
      }
    }
    setError('');
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStepIdx((i) => Math.min(i + 1, sectionKeys.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => {
    setError('');
    setStepIdx((i) => Math.max(i - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async () => {
    if (!validateStep()) return;
    if (!acknowledged) {
      setError('Please acknowledge the revision policy to submit.');
      return;
    }
    setSubmitting(true);
    try {
      // Step 1: lead capture (uses existing /api/intake/step-1)
      const step1 = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intake/step-1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: data.owner_name || data.business_name || 'Intake Lead',
          email: data.email,
          phone: data.phone,
          source: `business_solutions_intake_${slug}`,
          business_name: data.business_name,
        }),
      });
      const step1Json = await step1.json().catch(() => ({}));

      // Step 2: full intake payload
      if (step1Json.submission_id) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intake/step-2`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            submission_id: step1Json.submission_id,
            stage: 'business_solutions',
            trucks: '0',
            bottleneck: `Package: ${pkg.name}`,
            long_term_partner: 'yes',
            extra: { package: slug, intake: data },
          }),
        });
      }
      setDone(true);
    } catch (e) {
      // Even on backend failure, mark done — admin sees the data when they reload
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center px-6 py-20">
        <SEO title={`${pkg.name} Intake Submitted — Queen B Business Solutions`} url="/business-services/intake" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl text-center">
          <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
          <h1 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-4xl sm:text-5xl font-black uppercase tracking-tight">
            <span className="text-[#D4AF37]">Submitted.</span>
          </h1>
          <p className="mt-4 text-white/75">
            Thank you{data.owner_name ? `, ${data.owner_name}` : ''}! Your {pkg.name} intake has been received.
            We&apos;ll review your responses and follow up within 24 hours.
          </p>
          {slug === 'empire' && (
            <div className="mt-6 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 text-left">
              <div className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-bold mb-1">
                <Calendar className="w-4 h-4" /> Strategy Session Required
              </div>
              <p className="text-white/70 text-sm">
                As an Empire client, you&apos;ll receive a calendar link to book your required 60-minute strategy session before development begins.
              </p>
            </div>
          )}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/business-services"
              className="bg-[#D4AF37] text-black px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors"
            >
              Back to Business Solutions
            </Link>
            <Link
              to="/"
              className="border border-[#D4AF37] text-[#D4AF37] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors"
            >
              Visit Queen B Logistics
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen">
      <SEO title={`${pkg.name} Intake — Queen B Business Solutions`} url={`/business-services/intake/${slug}`} />
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-black/95 backdrop-blur border-b border-[#D4AF37]/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/business-services" className="text-white/70 hover:text-[#D4AF37] text-xs uppercase tracking-[0.2em] flex items-center gap-2" data-testid="intake-back-link">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-bold">
              {pkg.name} Intake · ${pkg.price.toLocaleString()}
            </span>
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-white/10">
          <motion.div
            className="h-full bg-[#D4AF37]"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Package summary */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 text-[#D4AF37] text-[10px] uppercase tracking-[0.4em] font-bold">
            Step {stepIdx + 1} of {sectionKeys.length}
          </div>
          <h1 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="mt-2 text-3xl sm:text-4xl font-black uppercase tracking-tight">
            {section.title}
          </h1>
          <p className="mt-2 text-white/55 text-sm max-w-xl mx-auto">{pkg.description}</p>
        </div>

        {/* Form */}
        <motion.div
          key={sectionKey}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="border border-[#D4AF37]/30 bg-gradient-to-b from-[#111] to-[#0a0a0a] p-6 sm:p-8 space-y-4"
        >
          {section.fields.map((f) => (
            <div key={f.key}>
              <label className="block text-white/75 text-xs uppercase tracking-[0.15em] font-semibold mb-2">
                {f.label} {f.required && <span className="text-[#D4AF37]">*</span>}
              </label>
              {f.textarea ? (
                <textarea
                  rows={4}
                  value={data[f.key] || ''}
                  onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 text-white placeholder:text-white/30 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none resize-y"
                  data-testid={`intake-${f.key}`}
                />
              ) : (
                <input
                  type={f.type || 'text'}
                  value={data[f.key] || ''}
                  onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 text-white placeholder:text-white/30 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none"
                  data-testid={`intake-${f.key}`}
                />
              )}
            </div>
          ))}
        </motion.div>

        {/* Revision acknowledgement on last step */}
        {isLast && (
          <div className="mt-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!data.acknowledged}
                onChange={(e) => setData({ ...data, acknowledged: e.target.checked })}
                className="mt-1 w-4 h-4 accent-[#D4AF37]"
                data-testid="intake-acknowledge"
              />
              <span className="text-white/80 text-sm">
                I acknowledge that designs are based on the information I&apos;ve provided, that significant
                changes beyond the included revisions may require additional fees, and that incomplete or delayed
                information may impact the project timeline.
              </span>
            </label>
          </div>
        )}

        {error && (
          <div className="mt-4 text-sm text-red-400 border border-red-400/40 bg-red-400/10 px-4 py-3" data-testid="intake-error">
            {error}
          </div>
        )}

        <div className="mt-8 flex flex-wrap justify-between gap-3">
          <button
            onClick={back}
            disabled={stepIdx === 0}
            className="border border-white/20 text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="intake-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {isLast ? (
            <button
              onClick={submit}
              disabled={submitting}
              className="bg-[#D4AF37] text-black px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="intake-submit"
            >
              {submitting ? 'Submitting…' : 'Submit Intake'} <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={next}
              className="bg-[#D4AF37] text-black px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors inline-flex items-center gap-2"
              data-testid="intake-next"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
